import { API_BASE_URL, MEDIA_ORIGIN } from './env.js';
import {
  type AuthTokensResponse,
  type PhoneSavedResponse,
  type BannerResponse,
  type CartResponse,
  type CategoryTreeResponse,
  type ErrorResponse,
  type DeliveryZoneResponse,
  type OrderListItem,
  type OrderResponse,
  type ProductListItem,
  type ProductResponse,
  type RegionResponse,
  type SearchResponse,
  type ShipmentDetailResponse,
  type ShopResponse,
  type RecommendationResponse,
  type UserVehicleResponse,
  type VehicleMakeResponse,
  type VehicleModelResponse,
} from '@aps/contracts';

/**
 * API klienti.
 *
 * Barcha tiplar `@aps/contracts` dan keladi — bu yerda bironta ham
 * qo'lda yozilgan javob tipi yo'q. Backend kontraktni o'zgartirsa,
 * `tsc` shu yerda yiqiladi (avtopart-contracts skill).
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly payload: ErrorResponse,
  ) {
    super(payload.code);
    this.name = 'ApiError';
  }

  /** Mijozga ko'rsatiladigan matn — tilga qarab. */
  message_(locale: 'UZ' | 'RU'): string {
    return locale === 'RU' ? this.payload.messageRu : this.payload.messageUz;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Buyurtma yaratishda takroriy bosishni to'sadi. */
  idempotencyKey?: string;
  signal?: AbortSignal;
}

/**
 * Faqat lokal MinIO URL'ini brauzer ko'ra oladigan HTTPS tunnelga o'tkazadi.
 * Saqlangan media URL'lar va API javobining boshqa maydonlariga tegmaydi.
 */
export function publicMediaUrl(url: string, mediaOrigin?: string): string;
export function publicMediaUrl(url: null, mediaOrigin?: string): null;
export function publicMediaUrl(url: string | null, mediaOrigin?: string): string | null;
export function publicMediaUrl(url: string | null, mediaOrigin = MEDIA_ORIGIN): string | null {
  if (url === null || mediaOrigin === '') return url;

  try {
    const source = new URL(url);
    const target = new URL(mediaOrigin);
    if (
      source.protocol !== 'http:' ||
      !['localhost', '127.0.0.1'].includes(source.hostname) ||
      source.port !== '9010' ||
      !source.pathname.startsWith('/avtopart-media/') ||
      target.protocol !== 'https:' ||
      target.pathname !== '/' ||
      target.search !== '' ||
      target.hash !== '' ||
      target.username !== '' ||
      target.password !== ''
    ) {
      return url;
    }
    return `${target.origin}${source.pathname}${source.search}${source.hash}`;
  } catch {
    return url;
  }
}

function withPublicImage(item: ProductListItem): ProductListItem {
  return { ...item, imageUrl: publicMediaUrl(item.imageUrl) };
}

let accessToken: string | null = null;
let refreshToken: string | null = null;
let onUnauthenticated: (() => void) | null = null;

export function setTokens(tokens: AuthTokensResponse | null): void {
  accessToken = tokens?.accessToken ?? null;
  refreshToken = tokens?.refreshToken ?? null;
}

/**
 * `null` — obunani bekor qilish uchun.
 *
 * Bootstrap qayta o'rnatilganda (masalan test ichida) eski chaqiruvchi
 * osilib qolmasligi kerak — shuning uchun tozalash yo'li ochiq.
 */
export function setUnauthenticatedHandler(handler: (() => void) | null): void {
  onUnauthenticated = handler;
}

/**
 * TERMINAL `401`: yangilash uchun kalit yo'q, yangilash muvaffaqiyatsiz,
 * yoki yangilangandan keyin ham `401`. Tokenlar BIR MARTA tozalanadi va
 * chaqiruvchi (`Bootstrap`) xabardor qilinadi — u sessiyani `failed`ga
 * o'tkazadi va shaxsiy keshni tozalaydi.
 */
function handleTerminalUnauthorized(): void {
  setTokens(null);
  onUnauthenticated?.();
}

async function parse(response: Response): Promise<unknown> {
  if (response.status === 204) return null;
  const text = await response.text();
  if (text.length === 0) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

async function raw(path: string, options: RequestOptions): Promise<Response> {
  return fetch(`${API_BASE_URL}${path}`, {
    method: options.method ?? 'GET',
    headers: {
      ...(options.body === undefined ? {} : { 'content-type': 'application/json' }),
      ...(accessToken === null ? {} : { authorization: `Bearer ${accessToken}` }),
      ...(options.idempotencyKey === undefined
        ? {}
        : { 'idempotency-key': options.idempotencyKey }),
    },
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    ...(options.signal === undefined ? {} : { signal: options.signal }),
  });
}

/**
 * So'rov yuboradi.
 *
 * `401` kelganda BIR MARTA refresh qilinadi va so'rov takrorlanadi.
 * Ikkinchi marta ham `401` kelsa — sessiya tugagan, ilova qayta kirishga
 * o'tadi. Cheksiz sikl bo'lmasligi uchun takrorlash bir martalik.
 */
async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let response = await raw(path, options);

  if (response.status === 401) {
    if (refreshToken === null) {
      handleTerminalUnauthorized();
    } else {
      const refreshed = await raw('/api/v1/auth/refresh', {
        method: 'POST',
        body: { refreshToken },
      });

      if (refreshed.ok) {
        setTokens((await parse(refreshed)) as AuthTokensResponse);
        response = await raw(path, options);
        if (response.status === 401) {
          handleTerminalUnauthorized();
        }
      } else {
        handleTerminalUnauthorized();
      }
    }
  }

  const payload = await parse(response);

  if (!response.ok) {
    throw new ApiError(response.status, payload as ErrorResponse);
  }
  return payload as T;
}

/* ------------------------------ auth ------------------------------ */

export const api = {
  loginWithTelegram: (initData: string, signal?: AbortSignal): Promise<AuthTokensResponse> =>
    request('/api/v1/auth/telegram', {
      method: 'POST',
      body: { initData },
      ...(signal === undefined ? {} : { signal }),
    }),

  /**
   * Telegram ulashgan raqamni biriktirish.
   *
   * Serverga RAQAM emas, IMZOLANGAN yuklama boradi — u yerda bot
   * tokeni bilan tekshiriladi.
   */
  saveTelegramContact: (contactData: string): Promise<PhoneSavedResponse> =>
    request('/api/v1/auth/telegram/contact', { method: 'POST', body: { contactData } }),

  /* ---------------------------- katalog ---------------------------- */

  categories: (): Promise<CategoryTreeResponse[]> => request('/api/v1/categories'),

  /**
   * Bosh sahifa bannerlari.
   *
   * Ko'rsatish oynasini SERVER tekshiradi — telefon soati noto'g'ri
   * qo'yilgan mijoz eskirgan aksiyani ko'rib qolmasligi kerak.
   */
  banners: (): Promise<BannerResponse[]> => request('/api/v1/banners'),

  /** Faqat ochiq sozlamalar (kalit -> qiymat): aloqa, yetkazish izohi va h.k. */
  publicSettings: (): Promise<Record<string, string>> => request('/api/v1/settings'),

  products: (params: {
    categoryId?: string;
    limit?: number;
    cursor?: string;
    sort?: string;
  }): Promise<{ items: ProductListItem[]; nextCursor: string | null; total: number }> => {
    // `Object.entries` ixtiyoriy maydonlar tipidan `undefined` ni tashlab
    // yuboradi, shuning uchun sikl o'rniga aniq yozamiz — `tsc` ham,
    // o'quvchi ham nima uzatilayotganini ko'radi.
    const query = new URLSearchParams();
    if (params.categoryId !== undefined) query.set('categoryId', params.categoryId);
    if (params.sort !== undefined) query.set('sort', params.sort);
    if (params.cursor !== undefined) query.set('cursor', params.cursor);
    if (params.limit !== undefined) query.set('limit', String(params.limit));

    return request<{ items: ProductListItem[]; nextCursor: string | null; total: number }>(
      `/api/v1/products?${query.toString()}`,
    ).then((page) => ({ ...page, items: page.items.map(withPublicImage) }));
  },

  product: (slug: string): Promise<ProductResponse> =>
    request<ProductResponse>(`/api/v1/products/${encodeURIComponent(slug)}`).then((item) => ({
      ...item,
      imageUrl: publicMediaUrl(item.imageUrl),
      media: item.media.map((media) => ({
        ...media,
        originalUrl: publicMediaUrl(media.originalUrl),
        thumbUrl: publicMediaUrl(media.thumbUrl),
        cardUrl: publicMediaUrl(media.cardUrl),
        fullUrl: publicMediaUrl(media.fullUrl),
      })),
    })),

  search: (query: string, signal?: AbortSignal): Promise<SearchResponse> =>
    request<SearchResponse>(`/api/v1/search?q=${encodeURIComponent(query)}`, {
      ...(signal === undefined ? {} : { signal }),
    }).then((result) => ({ ...result, items: result.items.map(withPublicImage) })),

  /* --------------------------- ma'lumotnoma --------------------------- */

  regions: (): Promise<RegionResponse[]> => request('/api/v1/regions'),
  shops: (): Promise<ShopResponse[]> => request('/api/v1/shops'),
  deliveryZones: (): Promise<DeliveryZoneResponse[]> => request('/api/v1/delivery-zones'),

  /* ------------------------------ savat ------------------------------ */

  cart: (): Promise<CartResponse> => request('/api/v1/cart'),

  addToCart: (productId: string, qty: number): Promise<CartResponse> =>
    request('/api/v1/cart/items', { method: 'POST', body: { productId, qty } }),

  setCartQty: (itemId: string, qty: number): Promise<CartResponse> =>
    request(`/api/v1/cart/items/${itemId}`, { method: 'PATCH', body: { qty } }),

  clearCart: (): Promise<CartResponse> => request('/api/v1/cart', { method: 'DELETE' }),

  /* ---------------------------- buyurtma ---------------------------- */

  orders: (): Promise<{ items: OrderListItem[]; nextCursor: string | null; total: number }> =>
    request('/api/v1/orders'),

  order: (id: string): Promise<OrderResponse> => request(`/api/v1/orders/${id}`),

  createOrder: (body: unknown, idempotencyKey: string): Promise<OrderResponse> =>
    request('/api/v1/orders', { method: 'POST', body, idempotencyKey }),

  shipment: (id: string): Promise<ShipmentDetailResponse> => request(`/api/v1/shipments/${id}`),

  /* ------------------------------ garaj ------------------------------ */

  vehicleMakes: (): Promise<VehicleMakeResponse[]> => request('/api/v1/vehicle-makes'),

  vehicleModels: (makeId: string): Promise<VehicleModelResponse[]> =>
    request(`/api/v1/vehicle-models?makeId=${encodeURIComponent(makeId)}`),

  recommendations: (): Promise<RecommendationResponse> =>
    request<RecommendationResponse>('/api/v1/recommendations').then((result) => ({
      ...result,
      items: result.items.map(withPublicImage),
    })),

  garage: (): Promise<UserVehicleResponse[]> => request('/api/v1/garage'),

  addVehicle: (modelId: string, year: number): Promise<UserVehicleResponse[]> =>
    request('/api/v1/garage', { method: 'POST', body: { modelId, year, isActive: true } }),

  setActiveVehicle: (id: string): Promise<UserVehicleResponse[]> =>
    request(`/api/v1/garage/${id}/active`, { method: 'POST' }),

  removeVehicle: (id: string): Promise<UserVehicleResponse[]> =>
    request(`/api/v1/garage/${id}`, { method: 'DELETE' }),
};
